import { QueryFunction, useQuery } from '@tanstack/react-query';
import { BasicMarkets, createBasicMarket, getReadyMarkets } from 'app/entities/BasicMarket';
import { flow, pipe } from 'fp-ts/lib/function';
import * as Either from 'fp-ts/Either';
import * as Option from 'fp-ts/Option';
import { partial } from 'lodash/fp';
import staticMainnetLegacyMarkets from '../../staticmarkets/mainnetLegacy.json';
import staticMainnetLlamaMarkets from '../../staticmarkets/mainnetLlama.json';
import staticMainnetGrandMarkets from '../../staticmarkets/mainnetGrand.json';
import staticLocalMarkets from '../../staticmarkets/local.json';
import staticArbitrumMarkets from '../../staticmarkets/arbitrum.json';
import staticOptimismMarkets from '../../staticmarkets/optimism.json';
import staticBaseMarkets from '../../staticmarkets/base.json';
import staticLrtbaseMarkets from '../../staticmarkets/lrtbase.json';
import staticAnvilMarkets from '../../staticmarkets/anvil.json';
import { MarketsError } from './types';
import { getOrThrow } from 'app/features/util/either';
import { useSiloWeb3 } from '@silo/web3';
import { useAreSilosReady } from 'app/hooks/useAreSilosReady';
import { useMemo } from 'react';
import { GetConfigurationForSilo, useSiloConfiguration } from '@silo/config-legacy';
import { isNotNullNorUndefined } from '@silo/utils';
import { ProtocolInstance, ProtocolInstanceValue, StaticMarketsLegacy } from '@silo/types';
import produce from 'immer';

const allFallbackMarketsKey = 'allFallbackMarkets';

export type AllFallbackMarketsKey = [
  key: typeof allFallbackMarketsKey,
  protocolInstance: ProtocolInstance,
  getConfigurationForSilo: GetConfigurationForSilo
];

const fallbackMarketsFromStaticMarkets = ({
  protocolInstance,
  getConfigurationForSilo,
}: {
  protocolInstance: ProtocolInstance;
  getConfigurationForSilo: GetConfigurationForSilo;
}) => {
  const result = getStaticMarkets(protocolInstance, getConfigurationForSilo);
  const staticMarkets = getOrThrow(result);

  return staticMarkets.map(createBasicMarket.fromStaticMarket(protocolInstance));
};

const queryAllFallbackMarkets: QueryFunction<BasicMarkets, AllFallbackMarketsKey> = async ({
  queryKey: [_, protocolInstance, getConfigurationForSilo],
}) => {
  return fallbackMarketsFromStaticMarkets({
    protocolInstance,
    getConfigurationForSilo,
  });
};

const useQueryAllFallbackMarkets = () => {
  const { protocolInstance } = useSiloWeb3();
  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);

  const { data, error } = useQuery<BasicMarkets, MarketsError, BasicMarkets, AllFallbackMarketsKey>({
    queryKey: [allFallbackMarketsKey, protocolInstance!, getConfigurationForSilo],
    queryFn: queryAllFallbackMarkets,
    enabled: isNotNullNorUndefined(protocolInstance),
  });

  return { data, error: error ?? undefined };
};

export const useQueryReadyFallbackMarkets = (enabled?: boolean) => {
  const { data: allFallbackMarkets, error } = useQueryAllFallbackMarkets();
  const areSilosReadyQueries = useAreSilosReady(allFallbackMarkets?.map((market) => market.id) ?? [], enabled);

  const readyMarkets = useMemo(
    () =>
      pipe(
        allFallbackMarkets,
        Option.fromNullable,
        Option.map(getReadyMarkets(areSilosReadyQueries)),
        Option.toUndefined
      ),
    [areSilosReadyQueries, allFallbackMarkets]
  );

  return { data: readyMarkets, error };
};

export type StaticMarketsMap = ProtocolInstanceValue<any>;

const STATIC_MARKETS_MAP: StaticMarketsMap = {
  mainnetLegacy: staticMainnetLegacyMarkets,
  mainnetLlama: staticMainnetLlamaMarkets,
  mainnetGrand: staticMainnetGrandMarkets,
  arbitrum: staticArbitrumMarkets,
  optimism: staticOptimismMarkets,
  base: staticBaseMarkets,
  lrtbase: staticLrtbaseMarkets,
  anvil: staticAnvilMarkets,
  local: staticLocalMarkets,
};

export const baseGetStaticMarkets = (
  STATIC_MARKETS_MAP: StaticMarketsMap,
  protocolInstance: ProtocolInstance | undefined,
  getConfigurationForSilo: GetConfigurationForSilo
) =>
  pipe(
    protocolInstance,
    Either.fromNullable({ _tag: 'MissingProtocolInstanceError', protocolInstance } as MarketsError),
    Either.map((protocolInstance) => STATIC_MARKETS_MAP[protocolInstance]),
    Either.chain(
      flow(
        StaticMarketsLegacy.decode,
        Either.mapLeft((errors): MarketsError => ({ _tag: 'DecodeError', protocolInstance, error: errors }))
      )
    ),
    Either.map((staticMarkets) =>
      produce(staticMarkets, (draft) => {
        for (const { marketAssets, id: marketId } of draft) {
          const [base] = marketAssets;
          const { name, symbol } = base.inputToken;
          const config = getConfigurationForSilo(marketId);
          base.inputToken.name = config.name_MUST_SYNCHRONIZE ?? name;
          base.inputToken.symbol = config.symbol_MUST_SYNCHRONIZE ?? symbol;
        }
      })
    )
  );

export const getStaticMarkets = partial(baseGetStaticMarkets, [STATIC_MARKETS_MAP]);
